import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import AppLayout from '@/components/feature/AppLayout';
import { isBackendAvailable } from '@/api/client';
import { useToast } from '@/contexts/ToastContext';
import { mockSalesInvoices } from '@/mocks/billing';
import { salesService } from '@/services/salesService';
import { salesPaymentService } from '@/services/salesPaymentService';
import {
  mockPaymentReceipts,
  type PaymentMode,
  type ChequeStatus,
} from '@/mocks/payments';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { bankReconApi, type CashBankLedger } from '@/api/bankReconApi';

interface Company {
  id: string;
  name: string;
  address: string;
  gstin: string;
  phone: string;
  email: string;
  logo_url?: string | null;
}

interface LocationState {
  invoiceId: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  invoiceAmount: number;
  balanceDue: number;
  invoiceDate: string;
  // TDS profile snapshotted on the invoice (Stage 2) — informational fields that
  // pre-fill and drive the TDS section below, never re-derived from the client.
  tdsApplicable?: boolean;
  tdsSection?: string;
  tdsRate?: number;
  tdsAmount?: number;
  expectedReceipt?: number;
  tdsPanAtInvoice?: string;
}

const PAYMENT_MODES: { value: PaymentMode; label: string; icon: string }[] = [
  { value: 'CASH', label: 'Cash', icon: 'ri-money-rupee-circle-line' },
  { value: 'UPI', label: 'UPI', icon: 'ri-smartphone-line' },
  { value: 'CARD', label: 'Card', icon: 'ri-bank-card-line' },
  { value: 'CHEQUE', label: 'Cheque', icon: 'ri-file-paper-line' },
  { value: 'NEFT', label: 'NEFT', icon: 'ri-exchange-line' },
  { value: 'RTGS', label: 'RTGS', icon: 'ri-bank-line' },
];

const CHEQUE_STATUSES: { value: ChequeStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'CLEARED', label: 'Cleared' },
  { value: 'BOUNCED', label: 'Bounced' },
  { value: 'INSUFFICIENT_BALANCE', label: 'Insufficient Balance' },
];

// Mirrors the backend's getFinancialYearAndQuarter (Indian FY: Apr→Mar) — display only,
// the server recomputes and persists its own authoritative value on save.
function getFinancialYearAndQuarter(dateStr: string): { financialYear: string; quarter: string } {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { financialYear: '', quarter: '' };
  const month = d.getMonth();
  const year = d.getFullYear();
  let fyStart: number;
  let quarter: string;
  if (month >= 3) {
    fyStart = year;
    quarter = month <= 5 ? 'Q1' : month <= 8 ? 'Q2' : 'Q3';
  } else {
    fyStart = year - 1;
    quarter = 'Q4';
  }
  return { financialYear: `${fyStart}-${String(fyStart + 1).slice(-2)}`, quarter };
}

const QUARTER_LABEL: Record<string, string> = {
  Q1: 'Q1 (Apr-Jun)',
  Q2: 'Q2 (Jul-Sep)',
  Q3: 'Q3 (Oct-Dec)',
  Q4: 'Q4 (Jan-Mar)',
};

function numberToWords(n: number): string {
  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];
  if (n === 0) return 'Zero';
  const convert = (num: number): string => {
    if (num < 20) return ones[num];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '')
      );
    if (num < 1000)
      return (
        ones[Math.floor(num / 100)] +
        ' Hundred' +
        (num % 100 ? ' ' + convert(num % 100) : '')
      );
    if (num < 100000)
      return (
        convert(Math.floor(num / 1000)) +
        ' Thousand' +
        (num % 1000 ? ' ' + convert(num % 1000) : '')
      );
    if (num < 10000000)
      return (
        convert(Math.floor(num / 100000)) +
        ' Lakh' +
        (num % 100000 ? ' ' + convert(num % 100000) : '')
      );
    return (
      convert(Math.floor(num / 10000000)) +
      ' Crore' +
      (num % 10000000 ? ' ' + convert(num % 10000000) : '')
    );
  };
  return convert(Math.floor(n)) + ' Rupees Only';
}

interface SuccessModalProps {
  receiptNumber: string;
  onClose: () => void;
  onPrint: () => void;
}
function SuccessModal({ receiptNumber, onClose, onPrint }: SuccessModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
          <i className="ri-checkbox-circle-fill text-3xl text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-[#1e293b] mb-1">
          Payment Recorded!
        </h3>
        <p className="text-sm text-slate-500 mb-1">
          Receipt No:{' '}
          <span className="font-semibold text-[#1e293b]">{receiptNumber}</span>
        </p>
        <p className="text-xs text-slate-400 mb-6">
          Invoice has been updated accordingly.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onPrint} // ✅ Print button already connected
            className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-printer-line" /> Print Receipt
            <kbd className="text-[10px] bg-white/20 px-1 rounded">F10</kbd>
          </button>
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SalesPaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const state = location.state as LocationState | null;
  const [company, setCompany] = useState<Company | null>(null);
  const [invoiceContext, setInvoiceContext] = useState<LocationState | null>(
    state,
  );
  const redirectedRef = useRef(false);

  const [receiptNo] = useState(`RCT-${new Date().getFullYear()}-AUTO`);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentAmount, setPaymentAmount] = useState<number | ''>(0);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('CASH');
  const [referenceNo, setReferenceNo] = useState('');
  const [cardLastFour, setCardLastFour] = useState('');
  const [bankName, setBankName] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [chequeStatus, setChequeStatus] = useState<ChequeStatus>('PENDING');
  const [bounceReason, setBounceReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isTdsDeducted, setIsTdsDeducted] = useState(true);
  const [tdsAmountInput, setTdsAmountInput] = useState<number | ''>(0);
  const [certificateNo, setCertificateNo] = useState('');
  const [certificateDate, setCertificateDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [successReceipt, setSuccessReceipt] = useState<string | null>(null);
  const [cashBankAccountId, setCashBankAccountId] = useState<string>('');
  const [cashBankAccounts, setCashBankAccounts] = useState<CashBankLedger[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();
  const [adjustAgainstDue, setAdjustAgainstDue] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceOptions, setInvoiceOptions] = useState<
    Array<{
      id: string;
      invoiceNo: string;
      date: string;
      customerName: string;
      grandTotal: number;
      balanceDue: number;
    }>
  >([]);
  const [loadingInvoiceOptions, setLoadingInvoiceOptions] = useState(false);
  const [loadingInvoiceContext, setLoadingInvoiceContext] = useState(() =>
    Boolean(searchParams.get('invoiceId')),
  );

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/company/get`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const data = await response.json();
        if (data?.success && data?.data) {
          setCompany(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch company:', error);
      }
    };
    fetchCompany();
  }, []);

  // ── Load Cash/Bank Accounts for COA Selector ───────────────────────────
  useEffect(() => {
    // Cash in hand + the company's bank accounts (Settings → Bank Accounts)
    bankReconApi.getCashBankLedgers()
      .then(setCashBankAccounts)
      .catch((err) => console.warn('Could not load cash/bank accounts:', err));
  }, []);

  // ── Hydrate from query / location state ─────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const hydrateFromQuery = async () => {
      if (state) {
        setInvoiceContext(state);
        setLoadingInvoiceContext(false);
        return;
      }
      const invoiceId = searchParams.get('invoiceId');
      if (!invoiceId) {
        setSelectionMode(true);
        setLoadingInvoiceContext(false);
        return;
      }
      setLoadingInvoiceContext(true);
      try {
        const detail = await salesService.getInvoice(invoiceId);
        if (!mounted) return;
        const raw = detail as unknown as {
          id: string;
          invoiceNo?: string;
          invoice_no?: string;
          customerId?: string;
          customer_id?: string;
          partyName?: string;
          party_name?: string;
          grandTotal?: number;
          grand_total?: number;
          balanceDue?: number;
          balance_due?: number;
          date: string;
          tdsApplicable?: boolean;
          tdsSection?: string;
          tdsRate?: number;
          tdsAmount?: number;
          expectedReceipt?: number;
          tdsPanAtInvoice?: string;
        };
        const invoiceAmount = Number(raw.grandTotal ?? raw.grand_total ?? 0);
        const balanceDue = Number(
          raw.balanceDue ?? raw.balance_due ?? invoiceAmount,
        );
        setInvoiceContext({
          invoiceId: raw.id,
          invoiceNumber: raw.invoiceNo ?? raw.invoice_no ?? '-',
          customerId: raw.customerId ?? raw.customer_id ?? '',
          customerName: raw.partyName ?? raw.party_name ?? 'Customer',
          invoiceAmount,
          balanceDue,
          invoiceDate: raw.date,
          tdsApplicable: raw.tdsApplicable,
          tdsSection: raw.tdsSection,
          tdsRate: raw.tdsRate,
          tdsAmount: raw.tdsAmount,
          expectedReceipt: raw.expectedReceipt,
          tdsPanAtInvoice: raw.tdsPanAtInvoice,
        });
        setSelectionMode(false);
      } catch {
        if (!mounted) return;
        toast.error('Failed to load invoice details for payment.');
        navigate('/sales/invoices');
      } finally {
        if (mounted) setLoadingInvoiceContext(false);
      }
    };
    hydrateFromQuery();
    return () => {
      mounted = false;
    };
  }, [state, searchParams, navigate, toast]);

  useEffect(() => {
    if (!invoiceContext) return;
    if (invoiceContext.tdsApplicable) {
      // Pre-fill Amount = expected receipt (balance minus TDS), TDS toggle ON —
      // the user is confirming what actually landed in the bank, not re-deciding TDS.
      const remainingTds = Math.max(
        0,
        Math.min(invoiceContext.tdsAmount ?? 0, invoiceContext.balanceDue),
      );
      setPaymentAmount(Math.max(0, invoiceContext.balanceDue - remainingTds));
      setIsTdsDeducted(true);
      setTdsAmountInput(remainingTds);
    } else {
      setPaymentAmount(invoiceContext.balanceDue);
      setIsTdsDeducted(false);
      setTdsAmountInput(0);
    }
  }, [invoiceContext]);

  // ── Load outstanding invoices when in selection mode ────────────────────
  useEffect(() => {
    let mounted = true;
    if (!selectionMode)
      return () => {
        mounted = false;
      };
    const loadOutstanding = async () => {
      try {
        setLoadingInvoiceOptions(true);
        const resp = await salesService.listInvoices({
          page: 1,
          limit: 300,
          warehouseId: selectedWarehouseId || undefined,
        });
        if (!mounted) return;
        const rows = Array.isArray(
          (resp as unknown as { items?: unknown[] }).items,
        )
          ? (resp as unknown as { items: unknown[] }).items
          : [];
        const mapped = rows
          .map((row) => {
            const r = row as {
              id: string;
              invoiceNo?: string;
              invoice_no?: string;
              date?: string;
              invoice_date?: string;
              partyName?: string;
              party_name?: string;
              customerName?: string;
              customer_name?: string;
              grandTotal?: number;
              grand_total?: number;
              balanceDue?: number;
              balance_due?: number;
              paymentStatus?: string;
              payment_status?: string;
            };
            return {
              id: r.id,
              invoiceNo: r.invoiceNo ?? r.invoice_no ?? '-',
              date: r.date ?? r.invoice_date ?? '',
              customerName:
                r.partyName ??
                r.party_name ??
                r.customerName ??
                r.customer_name ??
                '-',
              grandTotal: Number(r.grandTotal ?? r.grand_total ?? 0),
              balanceDue: Number(r.balanceDue ?? r.balance_due ?? 0),
              paymentStatus: (
                r.paymentStatus ??
                r.payment_status ??
                ''
              ).toUpperCase(),
            };
          })
          .filter(
            (r) =>
              r.balanceDue > 0 ||
              r.paymentStatus === 'UNPAID' ||
              r.paymentStatus === 'PARTIAL',
          );
        setInvoiceOptions(mapped);
      } catch {
        if (!mounted) return;
        toast.error('Failed to load outstanding invoices');
        setInvoiceOptions([]);
      } finally {
        if (mounted) setLoadingInvoiceOptions(false);
      }
    };
    void loadOutstanding();
    return () => {
      mounted = false;
    };
  }, [selectionMode, toast, selectedWarehouseId]);

  // ── INVOICE SELECTION SCREEN ─────────────────────────────────────────────
  if (!invoiceContext && selectionMode) {
    const filteredOptions = invoiceOptions.filter(
      (inv) =>
        inv.invoiceNo.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
        inv.customerName.toLowerCase().includes(invoiceSearch.toLowerCase()),
    );

    return (
      <AppLayout>
        <div className="p-6 bg-[#f8fafc] h-[calc(100vh-4rem)] flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5 shrink-0">
            <button
              onClick={() => navigate('/sales/payments')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-slate-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">
                Available Dues
              </h1>
              <p className="text-xs text-slate-400">
                Select an invoice with outstanding balance
              </p>
            </div>
          </div>

          {/* Full-width card */}
          <div className="bg-white border border-[#e2e8f0] rounded-xl flex-1 flex flex-col min-h-0">
            <div className="p-4 border-b border-[#e2e8f0] shrink-0">
              <div className="relative w-full max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="text"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  placeholder="Search by invoice no or customer..."
                  className="w-full h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                />
              </div>
            </div>

            {loadingInvoiceOptions ? (
              <div className="p-10 text-center text-slate-500 text-sm">
                Loading outstanding invoices...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">
                <i className="ri-file-list-3-line text-4xl block mb-2" />
                No outstanding invoices found
              </div>
            ) : (
              /* Full-width scrollable table */
              <div className="flex-1 overflow-auto hover-scrollbar bg-white min-h-0 rounded-b-xl">
                <table className="w-full min-w-[1000px] text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {[
                        'Invoice No',
                        'Date',
                        'Customer',
                        'Total Amount',
                        'Balance Due',
                        '',
                      ].map((h) => (
                        <th
                          key={h}
                          className={`px-5 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${h === 'Total Amount' || h === 'Balance Due' ? 'text-right' : ''}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOptions.map((inv, idx) => (
                      <tr
                        key={inv.id}
                        className={`border-b border-slate-50 hover:bg-indigo-50/30 transition-colors cursor-pointer ${idx % 2 === 1 ? 'bg-[#f8fafc]' : ''}`}
                        onClick={() =>
                          navigate(
                            `/sales/payments/new?invoiceId=${encodeURIComponent(inv.id)}`,
                          )
                        }
                      >
                        <td className="px-5 py-3.5 font-semibold text-[#4f46e5]">
                          {inv.invoiceNo}
                        </td>
                        <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">
                          {inv.date
                            ? new Date(inv.date).toLocaleDateString('en-IN')
                            : inv.date}
                        </td>
                        <td className="px-5 py-3.5 font-medium text-[#1e293b]">
                          {inv.customerName}
                        </td>
                        <td className="px-5 py-3.5 text-right text-slate-700">
                          {formatINR(inv.grandTotal)}
                        </td>
                        <td className="px-5 py-3.5 text-right font-bold text-red-600">
                          {formatINR(inv.balanceDue)}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/sales/payments/new?invoiceId=${encodeURIComponent(inv.id)}`,
                              );
                            }}
                            className="h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadingInvoiceContext) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center bg-[#f8fafc] p-6">
          <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
            <i className="ri-loader-4-line animate-spin text-[#4f46e5]" />
            Loading payment invoice...
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!invoiceContext) return null;

  // ──Available Dues FORM ──────────────────────────────────────────────────
  const needsRef =
    paymentMode === 'UPI' || paymentMode === 'NEFT' || paymentMode === 'RTGS';
  const needsCheque = paymentMode === 'CHEQUE';
  const needsCard = paymentMode === 'CARD';
  const needsBounceReason =
    chequeStatus === 'BOUNCED' || chequeStatus === 'INSUFFICIENT_BALANCE';
  const paymentAmountValue = Number(paymentAmount) || 0;
  const adjustedDue = invoiceContext
    ? Math.max(
        invoiceContext.balanceDue - (adjustAgainstDue ? paymentAmountValue : 0),
        0,
      )
    : 0;

  // TDS section — only offered when the invoice itself was flagged TDS-applicable
  // (Stage 2). Section/rate are read-only, sourced from that invoice snapshot.
  const tdsSectionAvailable = Boolean(invoiceContext?.tdsApplicable);
  const tdsAmountValue = tdsSectionAvailable && isTdsDeducted ? Number(tdsAmountInput) || 0 : 0;
  const totalSettled = Number((paymentAmountValue + tdsAmountValue).toFixed(2));
  const balanceRemaining = invoiceContext
    ? Math.max(0, Number((invoiceContext.balanceDue - totalSettled).toFixed(2)))
    : 0;
  const { financialYear, quarter } = getFinancialYearAndQuarter(date);

  const validate = (): string | null => {
    if (paymentAmountValue <= 0) return 'Payment amount must be greater than 0';
    if (tdsAmountValue < 0) return 'TDS amount cannot be negative';
    if (invoiceContext && totalSettled > invoiceContext.balanceDue + 1)
      return `Amount received + TDS (${formatINR(totalSettled)}) cannot exceed balance due (${formatINR(invoiceContext.balanceDue)})`;
    if (needsRef && !referenceNo.trim())
      return 'Reference number is required for ' + paymentMode;
    if (needsCheque) {
      if (!bankName.trim()) return 'Bank name is required';
      if (!chequeNo.trim()) return 'Cheque number is required';
      if (!chequeDate) return 'Cheque date is required';
      if (needsBounceReason && !bounceReason.trim())
        return 'Bounce reason is required';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);

    if (isBackendAvailable()) {
      try {
        const created = await salesPaymentService.create({
          invoiceId: invoiceContext.invoiceId,
          date,
          paymentAmount: paymentAmountValue,
          paymentMode,
          referenceNo: referenceNo || undefined,
          cardLastFour: cardLastFour || undefined,
          bankName: bankName || undefined,
          chequeNo: chequeNo || undefined,
          chequeDate: chequeDate || undefined,
          chequeStatus: needsCheque ? chequeStatus : undefined,
          bounceReason: bounceReason || undefined,
          notes: notes || undefined,
          warehouseId: selectedWarehouseId || undefined,
          warehouseName: selectedWarehouseName || undefined,
          isTdsDeducted: tdsSectionAvailable && isTdsDeducted && tdsAmountValue > 0,
          tdsAmount: tdsAmountValue > 0 ? tdsAmountValue : undefined,
          certificateNo: certificateNo.trim() || undefined,
          certificateDate: certificateNo.trim() && certificateDate ? certificateDate : undefined,
          cashBankAccountId: cashBankAccountId || undefined,
        });
        setSuccessReceipt(created.receiptNumber);
      } catch (error) {
        const message =
          error instanceof AxiosError
            ? (error.response?.data as { message?: string } | undefined)
                ?.message || error.message
            : 'Unable to save payment';
        toast.error(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Offline mock
    setTimeout(() => {
      mockPaymentReceipts.push({
        id: `rct-${Date.now()}`,
        receiptNumber: receiptNo,
        date,
        invoiceId: invoiceContext.invoiceId,
        invoiceNumber: invoiceContext.invoiceNumber,
        customerId: invoiceContext.customerId,
        customerName: invoiceContext.customerName,
        invoiceAmount: invoiceContext.invoiceAmount,
        balanceDue: invoiceContext.balanceDue,
        paymentAmount: paymentAmountValue,
        paymentMode,
        referenceNo: referenceNo || undefined,
        cardLastFour: cardLastFour || undefined,
        bankName: bankName || undefined,
        chequeNo: chequeNo || undefined,
        chequeDate: chequeDate || undefined,
        chequeStatus: needsCheque ? chequeStatus : undefined,
        bounceReason: bounceReason || undefined,
        notes: notes || undefined,
        createdAt: new Date().toISOString(),
      });
      const invIdx = mockSalesInvoices.findIndex(
        (i) => i.id === invoiceContext.invoiceId,
      );
      if (invIdx !== -1) {
        const inv = mockSalesInvoices[invIdx];
        const newPaid = (inv.paidAmount ?? 0) + paymentAmountValue;
        const newBalance = inv.grandTotal - newPaid;
        mockSalesInvoices[invIdx] = {
          ...inv,
          paidAmount: newPaid,
          balanceDue: Math.max(0, newBalance),
          paymentMode: newBalance <= 0 ? 'CASH' : 'PARTIAL',
          paymentStatus: newBalance <= 0 ? 'PAID' : 'PARTIAL',
        } as typeof inv;
      }
      setSaving(false);
      setSuccessReceipt(receiptNo);
    }, 600);
  };

  const handlePrint = () => {
    const receiptNumber = successReceipt || receiptNo;
    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const companyName = company?.name || 'InvenPro';
    const companyAddress = company?.address || 'Inventory Management System';
    const companyPhone = company?.phone || '';
    const companyGstin = company?.gstin || '';
    const companyEmail = company?.email || '';
    const companyLogo = company?.logo_url || null;

    // ✅ LOGO URL - Agar base64 hai toh direct use karo, nahi toh path join karo
    let logoUrl = null;
    if (companyLogo) {
      if (companyLogo.startsWith('data:image')) {
        // ✅ Already base64 - direct use karo
        logoUrl = companyLogo;
      } else {
        // ✅ File path - base URL join karo
        const baseUrl = `${import.meta.env.VITE_API_URL}`;
        logoUrl = `${baseUrl}${companyLogo}`;
      }
    }

    const printContent = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>Payment Receipt - ${receiptNumber}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { 
        font-family: 'Arial', sans-serif; 
        padding: 40px; 
        color: #1e293b;
        background: #fff;
        max-width: 800px;
        margin: 0 auto;
      }
      .header { 
        text-align: center; 
        border-bottom: 3px solid #4f46e5; 
        padding-bottom: 20px; 
        margin-bottom: 25px;
      }
      .header-top {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        margin-bottom: 8px;
      }
      .company-logo {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid #4f46e5;
        background: #f8fafc;
      }
      .company-name-section {
        text-align: left;
      }
      .company { 
        font-size: 28px; 
        font-weight: bold; 
        color: #1e293b;
      }
      .company-sub { 
        font-size: 14px; 
        color: #64748b; 
        margin-top: 4px;
      }
      .company-details {
        font-size: 12px;
        color: #94a3b8;
        margin-top: 4px;
      }
      .receipt-title { 
        font-size: 18px; 
        color: #4f46e5; 
        font-weight: bold;
        margin-top: 8px;
        letter-spacing: 2px;
      }
      .receipt-no { 
        font-size: 14px; 
        color: #64748b;
        margin-top: 4px;
      }
      .details-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 20px;
        background: #f8fafc;
        padding: 16px 20px;
        border-radius: 8px;
        margin-bottom: 20px;
      }
      .detail-item { display: flex; justify-content: space-between; padding: 4px 0; }
      .detail-label { color: #64748b; font-size: 13px; }
      .detail-value { font-weight: 600; font-size: 13px; color: #1e293b; }
      .amount-box { 
        background: #f0fdf4; 
        border: 2px solid #86efac; 
        border-radius: 12px; 
        padding: 20px; 
        text-align: center; 
        margin: 20px 0;
      }
      .amount-big { font-size: 32px; font-weight: bold; color: #16a34a; }
      .amount-words { font-size: 13px; color: #64748b; margin-top: 6px; font-style: italic; }
      .mode-badge {
        display: inline-block;
        padding: 4px 12px;
        background: #e0e7ff;
        color: #4f46e5;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
      }
      .footer { margin-top: 40px; display: flex; justify-content: space-between; padding-top: 20px; border-top: 1px solid #e2e8f0; }
      .sign-box { text-align: center; width: 200px; }
      .sign-line { border-top: 1px solid #1e293b; padding-top: 8px; margin-top: 30px; font-size: 12px; color: #64748b; }
      .print-date {
        text-align: center;
        font-size: 11px;
        color: #94a3b8;
        margin-top: 20px;
        border-top: 1px solid #e2e8f0;
        padding-top: 12px;
      }
      @media print { body { padding: 20px; } }
    </style>
  </head>
  <body>
    <div class="header">
      <div class="header-top">
        ${
          logoUrl
            ? `
        <img src="${logoUrl}" alt="${companyName}" class="company-logo" />
        `
            : `
        <div style="width:60px; height:60px; border-radius:50%; background:#e0e7ff; display:flex; align-items:center; justify-content:center; font-size:24px; color:#4f46e5; border:2px solid #4f46e5;">
          ${companyName.charAt(0).toUpperCase()}
        </div>
        `
        }
        <div class="company-name-section">
          <div class="company">${companyName}</div>
          <div class="company-sub">${companyAddress}</div>
        </div>
      </div>
      <div class="company-details">
        ${companyPhone ? `📞 ${companyPhone}` : ''}
        ${companyEmail ? ` ✉️ ${companyEmail}` : ''}
        ${companyGstin ? ` | GST: ${companyGstin}` : ''}
      </div>
      <div class="receipt-title">PAYMENT RECEIPT</div>
      <div class="receipt-no">Receipt No: <strong>${receiptNumber}</strong></div>
    </div>

    <div class="details-grid">
      <div class="detail-item">
        <span class="detail-label">Date</span>
        <span class="detail-value">${currentDate}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Payment Mode</span>
        <span class="detail-value"><span class="mode-badge">${paymentMode}</span></span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Customer</span>
        <span class="detail-value">${invoiceContext.customerName}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Invoice No</span>
        <span class="detail-value">${invoiceContext.invoiceNumber}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Invoice Amount</span>
        <span class="detail-value">${formatINR(invoiceContext.invoiceAmount)}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Balance Due</span>
        <span class="detail-value" style="color: ${invoiceContext.balanceDue - paymentAmountValue <= 0 ? '#16a34a' : '#dc2626'};">
          ${formatINR(Math.max(0, invoiceContext.balanceDue - paymentAmountValue))}
        </span>
      </div>
    </div>

    ${
      referenceNo
        ? `
    <div style="margin-bottom: 12px; display: flex; justify-content: space-between; padding: 4px 0; background: #f1f5f9; padding: 8px 16px; border-radius: 6px;">
      <span style="color: #64748b; font-size: 13px;">Reference No</span>
      <span style="font-weight: 600; font-size: 13px; font-family: monospace;">${referenceNo}</span>
    </div>`
        : ''
    }

    ${
      needsCheque
        ? `
    <div style="margin-bottom: 12px; display: flex; justify-content: space-between; padding: 4px 0; background: #fffbeb; padding: 8px 16px; border-radius: 6px; border: 1px solid #fcd34d;">
      <span style="color: #64748b; font-size: 13px;">Cheque Details</span>
      <span style="font-weight: 600; font-size: 13px;">${bankName} · ${chequeNo} · ${chequeDate}</span>
    </div>`
        : ''
    }

    <div class="amount-box">
      <div style="font-size: 13px; color: #64748b; margin-bottom: 4px;">Amount Paid</div>
      <div class="amount-big">${formatINR(paymentAmountValue)}</div>
      <div class="amount-words">${numberToWords(paymentAmountValue)}</div>
    </div>

    ${notes ? `<p style="font-size: 13px; color: #64748b; margin: 12px 0; padding: 8px 16px; background: #f8fafc; border-radius: 6px;"><strong>Notes:</strong> ${notes}</p>` : ''}

    <div class="footer">
      <div class="sign-box">
        <div style="height: 40px;"></div>
        <div class="sign-line">Received By</div>
      </div>
      <div class="sign-box">
        <div style="height: 40px;"></div>
        <div class="sign-line">Authorised Signatory</div>
      </div>
    </div>

    <div class="print-date">
      Printed on ${new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
    </div>

    <div style="text-align: center; margin-top: 12px; font-size: 11px; color: #94a3b8;">
      This is a system generated receipt. No signature required.
    </div>
  </body>
  </html>
  `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } else {
      toast.error('Please allow popups to print receipt');
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/sales/invoices')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-slate-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">
                Available Dues
              </h1>
              <p className="text-xs text-slate-400">
                Receipt number is generated automatically on save
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Invoice details card */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                Invoice Details
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-400">Invoice No</p>
                  <p className="text-sm font-semibold text-[#4f46e5]">
                    {invoiceContext.invoiceNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Customer</p>
                  <p className="text-sm font-semibold text-[#1e293b]">
                    {invoiceContext.customerName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Invoice Amount</p>
                  <p className="text-sm font-semibold text-[#1e293b]">
                    {formatINR(invoiceContext.invoiceAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Balance Due</p>
                  <p className="text-sm font-bold text-red-600">
                    {formatINR(invoiceContext.balanceDue)}
                  </p>
                </div>
              </div>
            </div>

            {/* Receipt info */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                Receipt Info
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Receipt Number
                  </label>
                  <input
                    type="text"
                    value={receiptNo}
                    readOnly
                    className="w-full h-9 px-3 text-sm bg-slate-50 border border-[#e2e8f0] rounded-lg text-slate-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                  />
                </div>
              </div>
            </div>

            {/* Payment Amount */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                Payment Amount
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
                  ₹
                </span>
                <input
                  type="number"
                  value={paymentAmount === '' ? '' : Math.round(paymentAmount)}
                  placeholder="0"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setPaymentAmount('');
                      return;
                    }
                    const clamped = Math.min(
                      Math.max(0, invoiceContext.balanceDue - tdsAmountValue),
                      Math.max(0, Number(v) || 0),
                    );
                    setPaymentAmount(clamped);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
                      e.preventDefault();
                  }}
                  min={1}
                  max={Math.max(0, invoiceContext.balanceDue - tdsAmountValue)}
                  className="w-full h-11 pl-7 pr-3 text-lg font-bold border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              {paymentAmountValue > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {numberToWords(paymentAmountValue)}
                </p>
              )}
            </div>

            {/* Payment Mode */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                Payment Mode
              </p>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setPaymentMode(m.value)}
                    className={`flex items-center gap-2 h-10 px-3 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      paymentMode === m.value
                        ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                        : 'bg-white text-slate-600 border-[#e2e8f0] hover:border-[#4f46e5] hover:text-[#4f46e5]'
                    }`}
                  >
                    <i className={`${m.icon} text-sm`} />
                    {m.label}
                  </button>
                ))}
              </div>

              {needsRef && (
                <div className="mt-4">
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Reference No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    placeholder={
                      paymentMode === 'UPI'
                        ? 'UPI Transaction ID'
                        : 'NEFT/RTGS Reference No'
                    }
                    className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                  />
                </div>
              )}

              {needsCard && (
                <div className="mt-4">
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Last 4 Digits (optional)
                  </label>
                  <input
                    type="text"
                    value={cardLastFour}
                    onChange={(e) =>
                      setCardLastFour(
                        e.target.value.replace(/\D/g, '').slice(0, 4),
                      )
                    }
                    placeholder="XXXX"
                    maxLength={4}
                    className="w-40 h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] font-mono tracking-widest"
                  />
                </div>
              )}

              {needsCheque && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        Bank Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="e.g. HDFC Bank"
                        className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        Cheque No <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={chequeNo}
                        onChange={(e) => setChequeNo(e.target.value)}
                        placeholder="CHQ-XXXXXX"
                        className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        Cheque Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={chequeDate}
                        onChange={(e) => setChequeDate(e.target.value)}
                        className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        Cheque Status
                      </label>
                      <select
                        value={chequeStatus}
                        onChange={(e) =>
                          setChequeStatus(e.target.value as ChequeStatus)
                        }
                        className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] bg-white"
                      >
                        {CHEQUE_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {needsBounceReason && (
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={bounceReason}
                        onChange={(e) => setBounceReason(e.target.value)}
                        placeholder="Reason for bounce / insufficient balance..."
                        className="w-full h-9 px-3 text-sm border border-red-200 rounded-lg focus:outline-none focus:border-red-400 bg-red-50"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* COA Ledger Account Selector */}
              {cashBankAccounts.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <i className="ri-bank-line text-[#4f46e5]" />
                      <span>Deposit Account (COA)</span>
                    </label>
                    <span className="text-[11px] text-slate-400">Optional · Defaults to {paymentMode === 'CASH' ? 'Cash' : 'Bank'}</span>
                  </div>
                  <select
                    value={cashBankAccountId}
                    onChange={(e) => setCashBankAccountId(e.target.value)}
                    className="w-full h-9 px-3 text-xs bg-slate-50 border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-slate-700 font-medium"
                  >
                    <option value="">Auto (Default {paymentMode === 'CASH' ? 'Cash in Hand' : 'Primary Bank Account'})</option>
                    {cashBankAccounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        [{acc.code}] {acc.name} — ₹{(acc.currentBalance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* TDS Section — only shown when the invoice itself was TDS-applicable */}
            {tdsSectionAvailable && (
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">TDS Section</p>

                <div className="flex items-center gap-3 p-3 rounded-lg border bg-indigo-50/50 border-indigo-200 mb-3">
                  <button
                    type="button"
                    onClick={() => setIsTdsDeducted((v) => !v)}
                    className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4f46e5]/40 ${
                      isTdsDeducted ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 ${
                        isTdsDeducted ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <p className="text-sm font-medium text-[#1e293b]">
                    Is TDS Deducted by Customer? <span className="font-semibold">{isTdsDeducted ? 'YES' : 'NO'}</span>
                  </p>
                </div>

                {isTdsDeducted && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">
                        TDS Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">₹</span>
                        <input
                          type="number"
                          value={tdsAmountInput === '' ? '' : tdsAmountInput}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTdsAmountInput(v === '' ? '' : Math.max(0, Number(v) || 0));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                          }}
                          className="w-full h-9 pl-7 pr-3 text-sm font-semibold border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">Auto-calculated from the invoice — editable for rounding differences.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-400">TDS Section</p>
                        <p className="text-sm font-semibold text-[#1e293b]">{invoiceContext.tdsSection || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">TDS Rate</p>
                        <p className="text-sm font-semibold text-[#1e293b]">{invoiceContext.tdsRate ?? '—'}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Customer PAN</p>
                        <p className="text-sm font-semibold text-[#1e293b] font-mono">{invoiceContext.tdsPanAtInvoice || 'NOT ON RECORD'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Financial Year</p>
                        <p className="text-sm font-semibold text-[#1e293b]">{financialYear}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-slate-400">Quarter</p>
                      <p className="text-sm font-semibold text-[#1e293b]">{QUARTER_LABEL[quarter] || quarter}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">TDS Certificate No</label>
                        <input
                          type="text"
                          value={certificateNo}
                          onChange={(e) => setCertificateNo(e.target.value)}
                          placeholder="Leave blank if not yet received"
                          className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Certificate Date</label>
                        <input
                          type="date"
                          value={certificateDate}
                          onChange={(e) => setCertificateDate(e.target.value)}
                          disabled={!certificateNo.trim()}
                          className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] disabled:bg-slate-50 disabled:text-slate-400"
                        />
                      </div>
                    </div>

                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
                      <i className="ri-information-line mt-0.5 shrink-0" />
                      <span>
                        TDS credit of {formatINR(tdsAmountValue)} will be recorded. Claim in Income Tax Return for FY {financialYear}.
                        Status: {certificateNo.trim() ? 'RECEIVED' : 'CERTIFICATE_PENDING'} (until certificate received).
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Summary */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Payment Summary</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Amount Received</span>
                  <span className="font-semibold text-[#1e293b]">{formatINR(paymentAmountValue)}</span>
                </div>
                {tdsAmountValue > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">TDS Deducted</span>
                    <span className="font-semibold text-[#1e293b]">{formatINR(tdsAmountValue)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-1.5 border-t border-[#e2e8f0]">
                  <span className="font-semibold text-[#1e293b]">Total Settled</span>
                  <span className="font-bold text-[#4f46e5]">{formatINR(totalSettled)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Balance Remaining</span>
                  <span className={`font-semibold ${balanceRemaining <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatINR(balanceRemaining)}
                  </span>
                </div>
              </div>
              {balanceRemaining <= 0 && totalSettled > 0 && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <i className="ri-checkbox-circle-fill" /> Payment fully settles the invoice
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] resize-none"
              />
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 flex items-center justify-center gap-2 rounded-xl bg-[#4f46e5] text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-3-line" /> Save Payment{' '}
                  <kbd className="text-[10px] bg-white/20 px-1.5 rounded ml-1">
                    F9
                  </kbd>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {successReceipt && (
        <SuccessModal
          receiptNumber={successReceipt}
          onClose={() => navigate('/sales/invoices')}
          onPrint={() => {
            handlePrint();
            navigate('/sales/invoices');
          }}
        />
      )}
    </AppLayout>
  );
}
